import { useState } from "react";
import { Check, Copy, Loader2, Share2, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useDemo } from "@/store/DemoStore";
import { daysUntil, fmtDate, fmtShort, returnDate } from "@/lib/trip";

const loadImg = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

function wrapText(ctx, text, x, y, maxWidth, lineHeight) {
  const words = (text || "").split(" ");
  let line = "";
  const lines = [];
  for (const w of words) {
    const test = line ? `${line} ${w}` : w;
    if (ctx.measureText(test).width > maxWidth && line) {
      lines.push(line);
      line = w;
    } else line = test;
  }
  if (line) lines.push(line);
  const startY = y - ((lines.length - 1) * lineHeight) / 2;
  lines.forEach((l, i) => ctx.fillText(l, x, startY + i * lineHeight));
}

async function buildCountdownBlob(trip) {
  if (document.fonts?.ready) {
    try { await document.fonts.ready; } catch { /* ignore */ }
  }
  const W = 1080;
  const H = 1080;
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#0b192c";
  ctx.fillRect(0, 0, W, H);
  try {
    const bg = await loadImg(trip.image || "/images/sunset.jpg");
    const r = Math.max(W / bg.width, H / bg.height);
    const w = bg.width * r;
    const h = bg.height * r;
    ctx.drawImage(bg, (W - w) / 2, (H - h) / 2, w, h);
  } catch { /* keep navy background */ }

  const grad = ctx.createLinearGradient(0, 0, 0, H);
  grad.addColorStop(0, "rgba(11,25,44,0.6)");
  grad.addColorStop(0.4, "rgba(11,25,44,0.25)");
  grad.addColorStop(1, "rgba(11,25,44,0.95)");
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, W, H);

  try {
    const logo = await loadImg("/images/mytrip-logo-white.png");
    const lw = 260;
    const lh = logo.height * (lw / logo.width);
    ctx.drawImage(logo, (W - lw) / 2, 70, lw, lh);
  } catch { /* ignore missing logo */ }

  const d = daysUntil(trip.departure);
  ctx.textAlign = "center";

  ctx.fillStyle = "#ffffff";
  ctx.font = "800 300px 'Plus Jakarta Sans', sans-serif";
  ctx.fillText(d < 0 ? "0" : String(d), W / 2, 500);

  ctx.font = "700 58px 'Plus Jakarta Sans', sans-serif";
  ctx.fillStyle = "#C9A96E";
  ctx.fillText(d === 1 ? "DAY TO GO" : "DAYS TO GO", W / 2, 600);

  ctx.fillStyle = "#ffffff";
  ctx.font = "700 72px 'Plus Jakarta Sans', sans-serif";
  wrapText(ctx, trip.title || trip.destination, W / 2, 760, W - 160, 84);

  ctx.font = "500 44px 'Plus Jakarta Sans', sans-serif";
  ctx.fillStyle = "rgba(255,255,255,0.88)";
  ctx.fillText(`${fmtShort(trip.departure)} – ${fmtDate(returnDate(trip))}`, W / 2, 880);

  ctx.font = "500 34px 'Plus Jakarta Sans', sans-serif";
  ctx.fillStyle = "rgba(255,255,255,0.7)";
  ctx.fillText("Powered by Topsail Travel", W / 2, H - 70);

  return new Promise((resolve) => canvas.toBlob((b) => resolve(b), "image/jpeg", 0.92));
}

export function ShareTripButton({ trip, className }) {
  const [busy, setBusy] = useState(false);
  const onShare = async () => {
    setBusy(true);
    try {
      const blob = await buildCountdownBlob(trip);
      if (!blob) throw new Error("no-blob");
      const slug = (trip.destination || trip.title || "trip").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
      const file = new File([blob], `${slug}-countdown.jpg`, { type: "image/jpeg" });
      const days = daysUntil(trip.departure);
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: trip.title, text: `${days < 0 ? 0 : days} days until ${trip.title}! ✈️` });
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = file.name;
        a.click();
        URL.revokeObjectURL(url);
        toast.success("Countdown image saved");
      }
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("Couldn't create the image. Please try again.");
    }
    setBusy(false);
  };
  return (
    <button onClick={onShare} disabled={busy} data-testid="share-trip-btn" className={className}>
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Share2 className="h-4 w-4" />}
      Share Trip
    </button>
  );
}

export function InviteFriendsButton({ trip, className, iconOnly = false, label = "Invite Friends" }) {
  const { tripInviteLink } = useDemo();
  const [open, setOpen] = useState(false);
  const [link, setLink] = useState("");
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const openDialog = async (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    setOpen(true);
    if (!link) {
      setLoading(true);
      try {
        const l = await tripInviteLink(trip.id);
        setLink(l || "");
      } catch {
        toast.error("Couldn't create the invite link");
      }
      setLoading(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Couldn't copy — long-press the link to copy it");
    }
  };

  const share = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: trip.title, text: `Come see my upcoming trip: ${trip.title} ✈️`, url: link });
      } catch (e) {
        if (e?.name !== "AbortError") copy();
      }
    } else copy();
  };

  return (
    <>
      <button onClick={openDialog} aria-label="Invite friends" data-testid={iconOnly ? "invite-friends-hero-btn" : "invite-friends-btn"} className={className}>
        <UserPlus className={iconOnly ? "h-5 w-5" : "h-4 w-4"} />
        {!iconOnly && label}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="w-[calc(100vw-1.5rem)] max-w-md rounded-2xl p-5 sm:p-7" data-testid="invite-friends-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl text-navy-900">Invite friends to your trip</DialogTitle>
            <DialogDescription>Share this link so friends and family can see your itinerary and reach your advisor. No prices are shown.</DialogDescription>
          </DialogHeader>
          {loading ? (
            <div className="flex items-center justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-navy-700" /></div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 p-2">
                <input readOnly value={link} data-testid="invite-link-input" className="min-w-0 flex-1 bg-transparent px-2 text-sm text-navy-900 outline-none" />
                <button onClick={copy} data-testid="invite-link-copy" className="inline-flex h-9 shrink-0 items-center gap-1.5 rounded-lg bg-white px-3 text-sm font-semibold text-navy-900 ring-1 ring-slate-200 transition-colors hover:bg-slate-100">
                  {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
                  {copied ? "Copied" : "Copy"}
                </button>
              </div>
              <button onClick={share} data-testid="invite-link-share" className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-navy-900 text-sm font-semibold text-white transition-colors hover:bg-navy-800">
                <Share2 className="h-4 w-4" /> Share link
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
